import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import sessionStore from '../stores/sessionStore';
import i18nStore from '../stores/i18nStore';
import {
  TextField,
  Button,
  Typography,
  Link,
  Paper,
  Container,
  Alert,
  Box,
  Divider
} from '@mui/material';
import { FaWhatsapp } from "react-icons/fa";
import { RiTelegram2Line } from "react-icons/ri";
import logger from '../utils/logger';
import { authErrorMessage } from '../auth/authErrors';

function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Инициализируем переводы при загрузке компонента
  useEffect(() => {
    const initI18n = async () => {
      // Если переводы еще не загружены
      if (!i18nStore.translations || Object.keys(i18nStore.translations).length === 0) {
        await i18nStore.initializeLanguage();
      }
    };

    initI18n();
  }, []);

  useEffect(() => {
    if (sessionStore.user) {
      navigate('/requests');
    }
  }, [sessionStore.user, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirm) {
      setError(i18nStore.translations?.register?.passwordMismatch || 'Пароли не совпадают');
      setLoading(false);
      return;
    }

    try {
      await sessionStore.registerByEmail(email, password);
      navigate('/verify-email-pending', { state: { email } });
    } catch (err) {
      logger.error('Registration failed', { name: err.name });
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} style={{ padding: 20, marginTop: 40 }}>
        <Typography variant="h6" style={{ marginBottom: 20 }} sx={{ color: 'text.primary' }}>
          Reldo - система управления недвижимостью.
        </Typography>

        <Typography variant="body1" style={{ marginBottom: 20, textAlign: 'center' }}>
          Регистрация
        </Typography>
        {error && (
          <Alert severity="error" style={{ marginBottom: 20 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleRegister}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={i18nStore.translations?.login?.email || 'Электронная почта'}
            autoComplete="email"
            autoFocus
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={i18nStore.translations?.register?.password || 'Пароль'}
            type="password"
            size="small"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={i18nStore.translations?.register?.confirmPassword || 'Подтвердите пароль'}
            type="password"
            autoComplete="new-password"
            size="small"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={loading}
          />
          <Typography variant="caption" display="block">Пароль должен содержать от 8 до 128 символов.</Typography>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            style={{ margin: '24px 0 16px' }}
            disabled={loading}>
           Зарегистрироваться
          </Button>
        </form>
        <Typography style={{ marginBottom: 20 }}>
          Уже есть учетная запись?{' '}
          <Link href="/login" variant="body2">
            Войти
          </Link>
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          Если вам требуется помощь, пишите:
        </Typography>
        <Box>
          <Link 
            href="https://wa.me/79882602218" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              textDecoration: 'none', 
              color: '#25D366', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              mb: 1
            }}
          >
            <FaWhatsapp style={{ fontSize: '18px' }} /> +7988-260-22-18
          </Link>
          <Link 
            href="https://t.me/reldo_crm" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              textDecoration: 'none', 
              color: '#0088cc', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '5px' 
            }}
          >
            <RiTelegram2Line style={{ fontSize: '18px' }} /> @reldo_crm
          </Link>
        </Box>
      </Paper>
    </Container>
  );
}

export default observer(Register);
